import { Routes } from "@angular/router";
import { authGuard } from "./core/guards/auth.guard";
import { teacherGuard } from "./core/guards/teacher.guard";
import { managerGuard } from "./core/guards/manager.guard";
import { studentGuard } from "./core/guards/student.guard";
import { candidateGuard } from "./core/guards/candidate.guard";
import {
  onboardingGuard,
  onboardingDoneGuard,
} from "./core/guards/onboarding.guard";

export const appRoutes: Routes = [
  {
    path: "home",
    loadComponent: () =>
      import("./pages/landing/landing.component").then((m) => m.LandingComponent),
  },
  {
    path: "login",
    loadComponent: () =>
      import("./pages/login/login.component").then((m) => m.LoginComponent),
  },
  {
    /*
     * Entrada de quem foi convidado (spec 018) — aluno **ou** professora, daí
     * o `authGuard` no lugar do `studentGuard`. Não leva `onboardingGuard`: é
     * o destino dele. Leva o inverso, para quem já concluiu e digita a URL
     * voltar ao painel do seu papel, em vez de reencontrar um formulário
     * preenchido que, submetido de novo, não teria efeito nenhum.
     */
    path: "boas-vindas",
    loadComponent: () =>
      import("./pages/boas-vindas/boas-vindas").then((m) => m.BoasVindas),
    canActivate: [authGuard, onboardingDoneGuard],
  },
  {
    path: "recuperar-senha",
    loadComponent: () =>
      import("./pages/recuperar-senha/recuperar-senha").then(
        (m) => m.RecuperarSenha,
      ),
  },
  {
    // Painel do candidato do Trabalhe Conosco (spec 028).
    path: "candidato",
    loadComponent: () =>
      import("./pages/candidato/candidato").then((m) => m.Candidato),
    canActivate: [candidateGuard],
  },
  {
    // Vitrine pública: vagas abertas ou banco de talentos (spec 028).
    path: "trabalhe-conosco",
    loadComponent: () =>
      import("./pages/trabalhe-conosco/trabalhe-conosco").then(
        (m) => m.TrabalheConosco,
      ),
  },
  {
    path: "trabalhe-conosco/cadastro",
    loadComponent: () =>
      import("./pages/trabalhe-conosco/cadastro/cadastro").then(
        (m) => m.TrabalheConoscoCadastro,
      ),
  },
  {
    // Documentos jurídicos públicos (spec 029). "?versao=" abre uma anterior.
    path: "termos-de-uso",
    loadComponent: () =>
      import("./pages/documento-legal/documento-legal").then(
        (m) => m.DocumentoLegal,
      ),
    data: { legalType: "terms-of-use" },
  },
  {
    path: "privacidade",
    loadComponent: () =>
      import("./pages/documento-legal/documento-legal").then(
        (m) => m.DocumentoLegal,
      ),
    data: { legalType: "privacy-policy" },
  },
  {
    // Termo de tratamento de dados do Trabalhe Conosco (spec 028): público,
    // o aceite do cadastro aponta para cá antes de existir conta. Desde a
    // spec 029 é o documento "recruitment" do Painel Jurídico.
    path: "trabalhe-conosco/privacidade",
    loadComponent: () =>
      import("./pages/documento-legal/documento-legal").then(
        (m) => m.DocumentoLegal,
      ),
    data: { legalType: "recruitment" },
  },
  {
    path: "academy",
    loadComponent: () => import("./pages/academy/academy").then((m) => m.Academy),
  },
  {
    path: "teacher-dashboard",
    loadComponent: () =>
      import("./pages/teacher-dashboard/teacher-dashboard.component").then(
        (m) => m.TeacherDashboardComponent,
      ),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    path: "students",
    loadComponent: () => import("./pages/students/students").then((m) => m.Students),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    path: "supplies",
    loadComponent: () =>
      import("./pages/supply-list/supply-list").then((m) => m.SupplyList),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    path: "generate/:id",
    loadComponent: () => import("./pages/generate/generate").then((m) => m.Generate),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    path: "generation/:id",
    loadComponent: () =>
      import("./pages/generation-monitor/generation-monitor").then(
        (m) => m.GenerationMonitor,
      ),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    path: "prompt-manager",
    loadComponent: () =>
      import("./pages/prompt-manager/prompt-manager").then((m) => m.PromptManager),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    // Painel de vídeo aulas da professora (spec 027). Rota fixa: vem antes
    // da /video-aulas/:id para "gerenciar" não ser lido como id.
    path: "video-aulas/gerenciar",
    loadComponent: () =>
      import("./pages/video-aulas-gerenciar/video-aulas-gerenciar").then(
        (m) => m.VideoAulasGerenciar,
      ),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    // Vídeo aulas do aluno: a árvore do nível dele (spec 027).
    path: "video-aulas",
    loadComponent: () =>
      import("./pages/video-aulas/video-aulas").then((m) => m.VideoAulas),
    canActivate: [studentGuard, onboardingGuard],
  },
  {
    // Uma aula. `authGuard` porque a professora também abre ("Ver como
    // aluno"); quem pode ver qual aula é decidido no backend.
    path: "video-aulas/:id",
    loadComponent: () =>
      import("./pages/video-aula/video-aula").then((m) => m.VideoAula),
    canActivate: [authGuard, onboardingGuard],
  },
  {
    path: "agenda",
    loadComponent: () => import("./pages/agenda/agenda").then((m) => m.Agenda),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    path: "aulas",
    loadComponent: () => import("./pages/aulas/aulas").then((m) => m.Aulas),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    path: "atualizacoes",
    loadComponent: () =>
      import("./pages/perfil/perfil").then((m) => m.Perfil),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    // Edição do próprio perfil — aluno e professora (spec 011 RF13/RF14).
    path: "perfil",
    loadComponent: () =>
      import("./pages/meu-perfil/meu-perfil").then((m) => m.MeuPerfil),
    canActivate: [authGuard],
  },
  {
    // Recrutamento da gerente (spec 028): vagas e banco de talentos.
    path: "recrutamento",
    loadComponent: () =>
      import("./pages/recrutamento/recrutamento").then((m) => m.Recrutamento),
    canActivate: [managerGuard],
  },
  {
    path: "recrutamento/vagas/:id",
    loadComponent: () =>
      import("./pages/recrutamento/vaga-candidatos/vaga-candidatos").then(
        (m) => m.VagaCandidatos,
      ),
    canActivate: [managerGuard],
  },
  {
    // Painel Jurídico da gerente (spec 029).
    path: "juridico",
    loadComponent: () => import("./pages/juridico/juridico").then((m) => m.Juridico),
    canActivate: [managerGuard],
  },
  {
    path: "juridico/:type",
    loadComponent: () =>
      import("./pages/juridico/documento/juridico-documento").then(
        (m) => m.JuridicoDocumento,
      ),
    canActivate: [managerGuard],
  },
  {
    path: "teachers",
    loadComponent: () =>
      import("./pages/teachers/teachers").then((m) => m.Teachers),
    canActivate: [managerGuard],
  },
  {
    path: "teachers/:id",
    loadComponent: () =>
      import("./pages/teacher-detail/teacher-detail").then((m) => m.TeacherDetail),
    canActivate: [managerGuard],
  },
  {
    path: "financeiro",
    loadComponent: () =>
      import("./pages/financeiro/financeiro").then((m) => m.Financeiro),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    // Contratos aceitos (spec 023 §7.4). Só a gerente: é registro probatório
    // sobre outras pessoas, não conteúdo do painel da professora.
    path: "financeiro/contratos",
    loadComponent: () =>
      import("./pages/contratos/contratos").then((m) => m.Contratos),
    canActivate: [managerGuard, onboardingGuard],
  },
  {
    // Painel financeiro do aluno (spec 012 RF1). Fica liberado mesmo com
    // pagamento pendente — é justamente aqui que ele regulariza.
    path: "meu-plano",
    loadComponent: () =>
      import("./pages/meu-plano/meu-plano").then((m) => m.MeuPlano),
    canActivate: [studentGuard, onboardingGuard],
  },
  {
    path: "student/:studentId",
    loadComponent: () =>
      import("./pages/student-panel/student-panel.component").then(
        (m) => m.StudentPanelComponent,
      ),
    // `authGuard` porque a gerente e a professora também abrem o painel de um
    // aluno; o `onboardingGuard` só retém quem é aluno e está incompleto.
    canActivate: [authGuard, onboardingGuard],
  },
  {
    // Era a única rota de conteúdo sem guard — as vizinhas já usavam authGuard.
    path: "student-content/:supplyId",
    loadComponent: () =>
      import("./pages/student-content-view/student-content-view.component").then(
        (m) => m.StudentContentViewComponent,
      ),
    canActivate: [authGuard, onboardingGuard],
  },
  {
    // Antiga porta do IPA. Depois dos Fixes 11 e 13 nada mais navegava para
    // ela, e a página tinha virado um menu de um item só apontando para
    // /artigos. Vira redirecionamento para não quebrar link salvo (RF10).
    path: "material/:id",
    redirectTo: "artigos",
  },
  // Artigos entram no lugar do IPA como material de apoio (spec 011 RF10).
  {
    path: "artigos",
    loadComponent: () => import("./pages/artigos/artigos").then((m) => m.Artigos),
    canActivate: [authGuard],
  },
  {
    // Rota fixa antes da paramétrica: /novo não pode cair em /:id.
    // Professora também escreve — o artigo dela entra na fila de aprovação
    // da gerente (spec 011 RF7 + Fix 21). Com `managerGuard` aqui, nenhum
    // artigo chegava a `pending` e o fluxo inteiro ficava inalcançável.
    path: "artigos/novo",
    loadComponent: () =>
      import("./pages/artigo-editor/artigo-editor").then((m) => m.ArtigoEditor),
    canActivate: [teacherGuard, onboardingGuard],
  },
  {
    path: "artigos/:id",
    loadComponent: () =>
      import("./pages/artigo-detalhe/artigo-detalhe").then(
        (m) => m.ArtigoDetalhe,
      ),
    canActivate: [authGuard],
  },
  {
    // A autoria é conferida no backend: a professora só edita o que escreveu.
    path: "artigos/:id/editar",
    loadComponent: () =>
      import("./pages/artigo-editor/artigo-editor").then((m) => m.ArtigoEditor),
    canActivate: [teacherGuard, onboardingGuard],
  },
  { path: "", redirectTo: "home", pathMatch: "full" },
  {
    path: "**",
    loadComponent: () => import("./pages/not-found/not-found").then((m) => m.NotFound),
  },
];
